import * as React from 'react'
import clsx from 'clsx'
import { ButtonBaseProps, ButtonBase } from '../ButtonBase'
import style from './index.module.scss'
import { capitalize } from '../../utils'

export interface TabClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if both `icon` and `label` are provided. */
  labelIcon: string
  /** Styles applied to the root element if the parent [`Tabs`](/material-ui/api/tabs/) has `textColor="inherit"`. */
  textColorInherit: string
  /** Styles applied to the root element if the parent [`Tabs`](/material-ui/api/tabs/) has `textColor="primary"`. */
  textColorPrimary: string
  /** Styles applied to the root element if the parent [`Tabs`](/material-ui/api/tabs/) has `textColor="secondary"`. */
  textColorSecondary: string
  /** State class applied to the root element if `selected={true}` (controlled by the Tabs component). */
  selected: string
  /** State class applied to the root element if `disabled={true}` (controlled by the Tabs component). */
  disabled: string
  /** Styles applied to the root element if `fullWidth={true}` (controlled by the Tabs component). */
  fullWidth: string
  /** Styles applied to the root element if `wrapped={true}`. */
  wrapped: string
  /** Styles applied to the root element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the root element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the root element if `size="large"`. */
  sizeLarge: string
  /** Styles applied to the wrapper element of `icon` if `icon` is provided. */
  iconWrapper: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  orientationVertical: string
  /** Styles applied to the root element if `orientation="horizontal"`. */
  orientationHorizontal: string
}

export interface TabProps extends Omit<ButtonBaseProps, 'onChange'> {
  /**
   * This prop isn't supported.
   * Use the `component` prop if you need to change the children structure.
   */
  children?: null
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<TabClasses>
  /**
   * The icon to display.
   */
  icon?: string | React.ReactElement
  /**
   * The position of the icon relative to the label.
   * @default 'top'
   */
  iconPosition?: 'top' | 'bottom' | 'start' | 'end'
  /**
   * The label element.
   */
  label?: React.ReactNode
  /**
   * You can provide your own value. Otherwise, we fallback to the child position index.
   */
  value?: any
  /**
   * Tab labels appear in a single row.
   * They can use a second line if needed.
   * @default false
   */
  wrapped?: boolean
  /**
   * If `true`, the  keyboard focus ripple is disabled.
   * @default false
   */
  disableFocusRipple?: boolean
  /**
   * internal props
   */
  onChange?: (event: React.FormEvent<HTMLButtonElement>, value: any) => void
  selected?: boolean
  selectionFollowsFocus?: boolean
  textColor?: string
  fullWidth?: boolean
  indicator?: 'secondary' | 'primary'
  variant?: 'standard' | 'pill'
  size?: 'small' | 'medium' | 'large'
  isVertical?: boolean
  isRtl?: boolean
  stacked?: boolean
}

export const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  function Tab(props, ref) {
    const {
      disabled = false,
      disableFocusRipple = false,
      icon: iconProp,
      iconPosition = 'top',
      value,
      wrapped = false,
      variant = 'standard',
      size = 'medium',
      isVertical = false,
      isRtl = false,
      stacked = true,
      label,
      onChange,
      onClick,
      onFocus,
      classes,
      // eslint-disable-next-line react/prop-types
      indicator,
      // eslint-disable-next-line react/prop-types
      fullWidth,
      // eslint-disable-next-line react/prop-types
      selected,
      // eslint-disable-next-line react/prop-types
      selectionFollowsFocus,
      // eslint-disable-next-line react/prop-types
      textColor = 'inherit',
      ...other
    } = props

    const ownerState = {
      disabled,
      selected,
      icon: !!iconProp,
      iconPosition,
      label: !!label,
      fullWidth,
      textColor,
      wrapped,
      variant,
      size,
      isVertical,
      isRtl,
      stacked,
    }

    const icon =
      iconProp && label && React.isValidElement(iconProp)
        ? React.cloneElement(iconProp, {
            // @ts-ignore
            className: clsx(classes?.iconWrapper, iconProp?.props?.className),
          })
        : iconProp
    const handleClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (!selected && onChange) {
        onChange(event, value)
      }
      if (onClick) {
        onClick(event)
      }
    }

    const handleFocus = (
      event: React.FocusEvent<HTMLButtonElement, Element>
    ) => {
      if (selectionFollowsFocus && !selected && onChange) {
        onChange(event, value)
      }
      if (onFocus) {
        onFocus(event)
      }
    }

    return (
      <ButtonBase
        focusRipple={!disableFocusRipple}
        classes={{
          root: clsx(
            classes?.root,
            style.root,
            ownerState.icon && [style.icon],
            ownerState.label && [style.label],
            // @ts-ignore
            style[`textColor-${ownerState.textColor}`],
            // @ts-ignore
            classes?.[`textColor${capitalize(ownerState.textColor)}`],
            ownerState.icon && ownerState.label && [classes?.labelIcon],
            ownerState.fullWidth && [style.fullWidth, classes?.fullWidth],
            style[`iconPosition-${ownerState.iconPosition}`],
            ownerState.wrapped && [style.wrapped, classes?.wrapped],
            ownerState.selected && [style.selected],
            ownerState.disabled && [classes?.disabled, style.disabled],
            ownerState.variant && style[`variant-${ownerState.variant}`],
            ownerState.stacked && style.stacked,
            ownerState.size && [
              style[ownerState.size],
              // @ts-ignore
              classes?.[`size${capitalize(ownerState.size)}`],
            ],
            ownerState.isVertical
              ? [style.vertical, classes?.[`orientationVertical`]]
              : [style.horizontal, classes?.[`orientationHorizontal`]],
            ownerState.isRtl && [style.rtl]
          ),
        }}
        ref={ref}
        role="tab"
        aria-selected={selected}
        disabled={disabled}
        onClick={handleClick}
        onFocus={handleFocus}
        tabIndex={selected ? 0 : -1}
        {...other}
      >
        {iconPosition === 'top' || iconPosition === 'start' ? (
          <React.Fragment>
            {icon}
            {label}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {label}
            {icon}
          </React.Fragment>
        )}
        {indicator}
      </ButtonBase>
    )
  }
)
